<template>
	<button
		:class="buttonClass"
		:style="buttonStyle"
		@mouseenter="hover = true"
		@mouseleave="hover = false"
		@click="handleClick($event)"
		:disabled="!enabled"
	>
		<img
			v-if="currentImageUrl"
			:src="currentImageUrl"
			:style="iconStyle"
			class="button-icon"
		/>
		{{ text }}
	</button>
</template>

<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
	text: {
		type: String,
		required: true,
	},
	width: {
		type: String,
		default: '100px',
	},
	height: {
		type: String,
		default: '40px',
	},
	fontSize: {
		type: String,
		default: '16px',
	},
	fontWeight: {
		type: String,
		default: '500',
	},
	padding: {
		type: String,
		default: '10px 10px',
	},
	imageUrl: {
		type: String,
		default: '',
	},
	disabledImageUrl: {
		type: String,
		default: '',
	},
	enabled: {
		type: Boolean,
		default: true,
	},
	enableBorder: {
		type: String,
		default: 'none',
	},
	disableBorder: {
		type: String,
		default: 'none',
	},
	colors: {
		type: Object,
		default: () => ({
			enabled: '#4189eb',
			disabled: '#AFB8BE',
			hover: '#3C7ED9',
			textEnabled: '#FFF',
			textDisabled: '#B8B8B8',
		}),
	},
	iconWidth: {
		type: String,
		default: '20px',
	},
	iconHeight: {
		type: String,
		default: '20px',
	},
	borderRadius: {
		type: String,
		default: '8px',
	},
});

const emit = defineEmits(['click']);
const hover = ref(false);
const currentImageUrl = computed(() => {
	return props.enabled ? props.imageUrl : props.disabledImageUrl;
});
const buttonClass = computed(() => {
	return {
		'button-enabled': props.enabled,
		'button-disabled': !props.enabled,
		'button-hover': hover.value,
	};
});
const buttonStyle = computed(() => {
	let backgroundColor = props.colors.disabled;
	let color = props.colors.textDisabled;

	if (props.enabled) {
		backgroundColor = hover.value ? props.colors.hover : props.colors.enabled;
		color = props.colors.textEnabled;
	}

	return {
		width: props.width,
		height: props.height,
		backgroundColor,
		color,
		fontSize: props.fontSize,
		fontWeight: props.fontWeight,
		padding: props.padding,
		cursor: props.enabled ? 'pointer' : 'not-allowed',
		border: props.enabled ? props.enableBorder : props.disableBorder,
		borderRadius: props.borderRadius,
	};
});

const iconStyle = computed(() => {
	return {
		width: props.iconWidth,
		height: props.iconHeight,
	};
});

const handleClick = event => {
	if (props.enabled) {
		emit('click', event);
	}
};
</script>

<style scoped>
.button-enabled {
	/* Additional styles for enabled state */
}

.button-disabled {
	/* Additional styles for disabled state */
	border: none;
}

.button-hover {
	/* Additional styles for hover state */
}

button {
	font-family: 'Noto Sans KR';
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 8px;
}
</style>
